import { CoachLauncherComponent } from './components/coach/coach-launcher/coach-launcher.component';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CrwdAppShell, CrwdAppUpdate, CrwdBanner, CrwdProgressChip, CrwdDropdown, CrwdIcon, CrwdMenu, CrwdSidebar, CrwdUserButton,
  CrwdUserMenuExtra, CrwdUserMenuItem, provideCrwdIcons,
} from '@crewdle/ui-kit';

import { AppComponent } from './app.component';
import { LocalFilesGrantBannerComponent } from './components/local-files-grant-banner/local-files-grant-banner.component';
import { OnboardingTourComponent } from './components/onboarding-tour/onboarding-tour.component';
import { NotificationsNudgeComponent } from './components/notifications-nudge/notifications-nudge.component';
import { APP_ICONS } from './utils/app-icons';
import { AppRoutingModule } from './app-routing.module';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideLottieOptions } from 'ngx-lottie';
import { provideServiceWorker } from '@angular/service-worker';
import { DesktopUpdatePillComponent } from './components/desktop-update-pill/desktop-update-pill.component';
import { AppHeaderModule } from './components/app-header/app-header.module';
import { CrewdleRouteStrategy } from './services/route-strategy/crewdle-route-strategy';
import { GlobalSearchComponent } from './components/global-search/global-search.component';
import { LocalAgentApprovalsComponent } from './components/local-agent-approvals/local-agent-approvals.component';
import { MemberBudgetBannerComponent } from './components/member-budget-banner/member-budget-banner.component';
import { OrgTabsComponent } from './components/org-tabs/org-tabs.component';
import { ionicBrandVars } from '@crewdle/white-label';
import { WHITE_LABEL_CONFIG } from '@crewdle/white-label/angular';
import { getFirestore } from 'firebase/firestore';
import { FirebaseService } from './services/firebase/firebase.service';
import { PRODUCT_ID } from './config/product';
import { provideChatTransloco } from './chat/transloco.providers';
import { environment } from 'src/environments/environment';
import { LocationStrategy } from '@angular/common';
import { OrgLocationStrategy } from 'src/app/services/org-url/org-url';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * True only inside the Capacitor (mobile) shell — see the provideServiceWorker
 * comment below. Structural probe rather than PlatformService because this runs
 * at module-definition time, outside any injector.
 */
function isCapacitorNative(): boolean {
  return !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor?.isNativePlatform?.();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    LocalFilesGrantBannerComponent,
    OnboardingTourComponent,
    NotificationsNudgeComponent,
    CoachLauncherComponent,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    AppRoutingModule,
    GlobalSearchComponent,
    LocalAgentApprovalsComponent,
    MemberBudgetBannerComponent,
    OrgTabsComponent,
    CrwdAppShell,
    CrwdAppUpdate, CrwdProgressChip,
    DesktopUpdatePillComponent,
    CrwdBanner,
    CrwdDropdown,
    CrwdIcon,
    CrwdMenu,
    CrwdSidebar,
    CrwdUserButton,
    CrwdUserMenuExtra,
    CrwdUserMenuItem,
  ],
  providers: [
    provideCrwdIcons(APP_ICONS),
    // Hosted Chat product surface (src/app/chat) keeps transloco; bridged to
    // ngx-translate's active language. See transloco.providers.ts.
    ...provideChatTransloco(),
    { provide: RouteReuseStrategy, useClass: CrewdleRouteStrategy },
    // The tab's organization lives in the address bar (/o/{orgId}/…) — OrgUrlState.
    { provide: LocationStrategy, useClass: OrgLocationStrategy },
    provideLottieOptions({
      player: () => import(/* webpackChunkName: 'lottie-web' */ 'lottie-web'),
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // PWA / offline shell. The Angular service worker downloads a complete
    // versioned copy of the app on first load and then serves it from cache,
    // updating atomically — which is also how the Electron desktop shell gets a
    // local copy of the remotely-loaded app (see AppVersionService).
    //
    // `enabled` keys off !isDevMode() rather than environment.production so an
    // optimized STAGING build registers too (staging uses environment.ts, where
    // `production` is false — tying it to that flag would have made the worker
    // untestable outside production). It also MUST stay off under `ng serve`,
    // which isDevMode() gives us for free.
    //
    // INVARIANT this depends on: every optimized angular.json configuration must
    // set `serviceWorker` so ngsw-worker.js is actually emitted. Adding an
    // optimized configuration WITHOUT it would make this register a script that
    // 404s on every boot. Currently satisfied by `production` and `staging-pwa`.
    //
    // ngsw-config.json is strict JSON (the builder JSON.parse()s it and the
    // schema sets additionalProperties:false), so the caching policy is
    // documented here instead:
    //   - assetGroups only. There are deliberately NO dataGroups: Firestore,
    //     Cloud Functions/callables, Storage and Datadog all run over transports
    //     the Firebase/Datadog SDKs manage themselves (long-lived streams,
    //     token refresh, their own offline persistence). Putting a service
    //     worker in front of them buys nothing and causes subtle auth failures
    //     when a stale token response gets replayed from cache.
    //   - `app` prefetches the shell: index.html, the root CSS/JS bundles (all
    //     lazy chunks live at the www root here) and assets/i18n — that is the
    //     "complete versioned copy" that lets the app run from cache.
    //   - Excluded from every group, on purpose: /pyodide/** (~60 MB of Python
    //     wasm + wheels, fetched by a worker only when a chat code block is
    //     Run), /version.json (cached => the deploy poll reads a stale hash
    //     forever), /auth-popup.html (the MSAL popup document), /api/** and
    //     /shared/apps/** (Hosting rewrites to functions, also excluded from
    //     navigationUrls so share links keep their server-injected OG tags),
    //     and /assets/emojis/** (3.8k files / 17 MB — they would quadruple
    //     ngsw.json, which is re-downloaded on every update check).
    // Registration timing is load-bearing, not a default worth inheriting.
    //
    // `registerWhenStable:30000` never fired here: an unauthenticated visit
    // redirects to auth.<domain>/authorize within ~5 s, and the app never
    // reaches Angular "stable" anyway (long-lived streams/listeners), so the
    // strategy always fell through to its 30 s cap — by which time the document
    // was long gone. Net effect: the worker was never installed for anonymous
    // visitors, and only ~30 s after sign-in for everyone else. Since the whole
    // point is that the desktop shell holds a complete local copy, we register
    // on a short fixed delay instead: 2 s is past first contentful paint but
    // comfortably before the auth redirect, and registration survives the
    // navigation away.
    //
    // Disabled inside Capacitor: the mobile shell BUNDLES the app, so a worker
    // there would shadow-cache assets that already ship in the binary (Android,
    // which serves from a secure http://localhost origin) or simply fail to
    // register at all (iOS capacitor:// has no service-worker support). The
    // Electron shell is the opposite case — it remote-loads over https and the
    // worker is exactly how it obtains its local copy — so it is NOT excluded.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode() && !isCapacitorNative(),
      registrationStrategy: 'registerWithDelay:2000',
    }),
    {
      // Partner white-label branding (colors, logos, favicon, title).
      // ionicBrandVars maps the brand onto this app's Ionic theme
      // (--color-accent + the --ion-color-primary family) instead of the
      // shared Tailwind tokens used by the consoles.
      provide: WHITE_LABEL_CONFIG,
      useFactory: (fb: FirebaseService) => ({
        firestore: getFirestore(fb.app),
        extraVars: ionicBrandVars,
      }),
      deps: [FirebaseService],
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
